import { useAccountContext } from '@seek/hirer-graphql-react';
import { useTranslations } from '@vocab/react';
import {
  IconDelete,
  Columns,
  Column,
  Text,
  IconEdit,
  useResponsiveValue,
} from 'braid-design-system';
import { useState, type FC, type PropsWithChildren } from 'react';

import { useConfig } from '../../../../hooks/useConfig';
import { IconButtonLink } from '../../IconButtonLink/IconButtonLink';
import translations from '../.vocab';
import { JobActionsLoader } from '../JobActionsLoader';
import { createEditDraftJobLink } from '../links';

import { DeleteDraftJobDialog } from './DeleteDraftJobDialog';

export interface DraftJobActionsProps {
  draftId: string;
  loading?: boolean;
}

export const DraftJobActions: FC<PropsWithChildren<DraftJobActionsProps>> = ({
  draftId,
  loading = false,
}) => {
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const { t } = useTranslations(translations);
  const { urlResolver } = useConfig();
  const jobTitleLinkUrl = createEditDraftJobLink(draftId);
  const responsiveValue = useResponsiveValue();
  const { accountContext } = useAccountContext();

  const columnWidth = responsiveValue({
    mobile: 'content',
    desktop: 'content',
  }) as undefined | 'content';

  if (loading) {
    return <JobActionsLoader isDraft={true} />;
  }

  return (
    <>
      <Columns space={['medium', 'small']} align="right">
        <Column width={columnWidth}>
          <IconButtonLink
            disabled={
              !accountContext?.currentAdvertiser?.permissionOptions
                .canCreateJobs
            }
            id={`${draftId}-delete`}
            tooltip={
              <>
                <Text>{t('Delete your draft ad')}</Text>
              </>
            }
            onClick={() => {
              setConfirmDelete(true);
            }}
            hoverBackground="formAccentSoft"
            eventName="job_action_pressed"
            eventAttributes={{ linkAction: 'delete' }}
            icon={
              <IconDelete titleId={`${draftId}-delete`} title={t('Delete')} />
            }
            dataTestId="deleteDraftJobButton"
          />
        </Column>
        <Column width={columnWidth}>
          <IconButtonLink
            id={`${draftId}-edit`}
            tooltip={<Text>{t('Edit your draft ad')}</Text>}
            href={urlResolver({
              path: jobTitleLinkUrl,
            })}
            hoverBackground="formAccentSoft"
            eventName="job_action_pressed"
            eventAttributes={{ linkAction: 'edit' }}
            icon={<IconEdit titleId={`${draftId}-edit`} title={t('Edit')} />}
          />
        </Column>
      </Columns>
      {accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs && (
        <DeleteDraftJobDialog
          draftId={draftId}
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
};
